import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import CustomButton from "../../../components/CustomButton";
import ContentCard from "../../../components/misc/ContentCard";
import TableWidget from "../../../components/tables/TableWidget";
import SectionTableWidget from "../../../components/tables/SectionTableWidget";
import { getRecallEntry } from "../../../redux/api/recall.api";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { clearRecallEntry } from "../../../redux/reducer/recall.reducer";
import { recallRoutes } from "../../../router/routesConstants";
import Text from "../../../styleguide/Text";
import RecallClientsActionButton from "./components/TableActionsButtonsComponents/RecallClientsActionButton";
import RecallReceiptsActionButton from "./components/TableActionsButtonsComponents/RecallReceiptsActionButton";
import RecallSectionActions from "./components/TableActionsButtonsComponents/RecallSectionActions";
import RecallClientBatchModal from "./components/RecallClientBatchModal";
import {
  getRecallClientsTableSections,
  getRecallReceiptsTableData,
  getRecallStockTableData,
  recallClientsTableHeader,
  recallReceiptsTableHeader,
  recallStockTableHeader,
} from "./components/tableConfigs/recallEntryDetailsTableConfig";

const sections = [
  { value: "clients", label: "Farmacii identificate" },
  { value: "stock", label: "Produse si stoc" },
  { value: "receipts", label: "Receptii de la furnizori" },
] as const;

function RecallEntryDetails() {
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const [selectedSection, setSelectedSection] =
    useState<(typeof sections)[number]["value"]>("clients");
  const {
    data: entry,
    isLoading,
    isError,
    errorMessage,
  } = useAppSelector((state) => state.recall.entry);
  const clientBatch = useAppSelector((state) => state.recall.clientBatch);
  const clientsTableSections = useMemo(
    () =>
      entry && selectedSection === "clients"
        ? getRecallClientsTableSections(entry)
        : [],
    [entry, selectedSection],
  );
  const warehouseStock = entry?.warehouseStock;
  const supplierReceipts = entry?.supplierReceipts;
  const stockTableData = useMemo(
    () =>
      selectedSection === "stock"
        ? getRecallStockTableData(warehouseStock ?? [])
        : [],
    [warehouseStock, selectedSection],
  );
  const receiptsTableData = useMemo(
    () =>
      selectedSection === "receipts"
        ? getRecallReceiptsTableData(supplierReceipts ?? [])
        : [],
    [supplierReceipts, selectedSection],
  );

  useEffect(() => {
    if (id) dispatch(getRecallEntry({ id }));
  }, [dispatch, id]);

  useEffect(() => {
    return () => {
      dispatch(clearRecallEntry());
    };
  }, [dispatch]);

  if (!entry) {
    return (
      <ContentCard cardTitle="Detalii retragere" isLoading={isLoading}>
        {isError && (
          <Text className="mb-4 text-danger" role="alert">
            {errorMessage}
          </Text>
        )}
        <CustomButton variant="outlined" navigate={`/${recallRoutes.recall}`}>
          Inapoi la retrageri
        </CustomButton>
      </ContentCard>
    );
  }

  const pharmacyCount =
    entry.routingGroups.reduce(
      (total, node) =>
        total +
        node.routes.reduce((count, route) => count + route.clients.length, 0),
      0,
    ) + entry.routingExceptions.length;

  return (
    <>
      {clientBatch.data && (
        <RecallClientBatchModal selection={clientBatch.data} />
      )}
      <ContentCard cardTitle={`Retragere ${entry.documentNumber}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Text>
            {pharmacyCount} farmacii identificate din istoricul de vanzari
          </Text>
          <CustomButton
            variant="outlined"
            navigate={`/${recallRoutes.recall}/${recallRoutes.receiveDocuments}`}>
            Primire documente
          </CustomButton>
        </div>
        {isError && (
          <Text className="mt-4 text-danger" role="alert">
            {errorMessage}
          </Text>
        )}
      </ContentCard>
      <ContentCard cardBodyClassName="flex">
        {sections.map((section, index) => (
          <CustomButton
            key={section.value}
            className="w-full"
            style={{
              borderTopLeftRadius: index === 0 ? undefined : 0,
              borderBottomLeftRadius: index === 0 ? undefined : 0,
              borderTopRightRadius:
                index === sections.length - 1 ? undefined : 0,
              borderBottomRightRadius:
                index === sections.length - 1 ? undefined : 0,
            }}
            variant={
              selectedSection === section.value ? "contained" : "outlined"
            }
            disabled={clientBatch.isLoading}
            onClick={() => setSelectedSection(section.value)}>
            {section.label}
          </CustomButton>
        ))}
      </ContentCard>
      {selectedSection === "clients" && (
        <SectionTableWidget
          key={entry.idCampaign}
          className="[&_thead_th]:text-left"
          tableTitle="Farmacii identificate"
          tableHeaderData={recallClientsTableHeader}
          tableSectionsData={clientsTableSections}
          withSeparateSections
          withTablePagination={false}
          renderBatchSize={50}
          withRouteParams={false}
          tableLoading={isLoading}
          ActionButtonsComponent={RecallClientsActionButton}
          SectionActionsComponent={RecallSectionActions}
        />
      )}
      {selectedSection === "stock" && (
        <TableWidget
          className="[&_thead_th]:text-left"
          tableTitle="Produse si stoc actual in depozit"
          tableHeaderData={recallStockTableHeader}
          tableItemsData={stockTableData}
          withTablePagination={false}
          withRouteParams={false}
        />
      )}
      {selectedSection === "receipts" && (
        <TableWidget
          className="[&_thead_th]:text-left"
          tableTitle="Receptii de la furnizori"
          tableHeaderData={recallReceiptsTableHeader}
          tableItemsData={receiptsTableData}
          withTablePagination={false}
          withRouteParams={false}
          ActionButtonsComponent={RecallReceiptsActionButton}
        />
      )}
    </>
  );
}

export default RecallEntryDetails;
